import { ApiError, NetworkError } from './errors.js';
import { memoryRefreshTokenStore } from './tokens.js';
/**
 * Client HTTP de l'API Repère.
 *
 * Le renouvellement de session est **silencieux et sérialisé** : plusieurs requêtes qui
 * échouent simultanément sur un jeton expiré déclenchent un seul rafraîchissement, puis
 * rejouent. Sans cette sérialisation, chaque requête consommerait un jeton de
 * rafraîchissement différent, et l'API — qui révoque toute la session au rejeu d'un jeton
 * déjà utilisé — déconnecterait l'utilisateur à chaque expiration.
 */
export class ApiClient {
    baseUrl;
    fetchImpl;
    refreshTokens;
    trustRefreshCookie;
    onSessionLost;
    webSocketFactory;
    /**
     * Champ réellement privé (`#`), et non `private` : ce dernier n'existe qu'à la
     * compilation. Le jeton d'accès resterait sinon lisible depuis n'importe quel code
     * JavaScript, et ressortirait dans une sérialisation accidentelle du client.
     */
    #accessToken = null;
    refreshing = null;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, '');
        this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
        this.refreshTokens = options.refreshTokens ?? memoryRefreshTokenStore();
        this.trustRefreshCookie = options.trustRefreshCookie ?? false;
        this.onSessionLost = options.onSessionLost;
        this.webSocketFactory =
            options.webSocket ?? ((url) => new WebSocket(url));
    }
    get isAuthenticated() {
        return this.#accessToken !== null || this.refreshTokens.read() !== null;
    }
    /**
     * Une session peut-elle exister sans que ce client la tienne en mémoire ? Oui dès que
     * le cookie de session est en jeu : il est `httpOnly`, invisible ici, et c'est le cas
     * nominal d'un onglet rouvert.
     */
    get sessionPotentielle() {
        return this.trustRefreshCookie || this.refreshTokens.read() !== null;
    }
    adopt(tokens) {
        this.#accessToken = tokens.accessToken;
        this.refreshTokens.write(tokens.refreshToken);
    }
    forget() {
        this.#accessToken = null;
        this.refreshTokens.clear();
    }
    async login(email, password) {
        const tokens = await this.request('/auth/login', {
            method: 'POST',
            body: { email, password },
            skipRefresh: true,
        });
        this.adopt(tokens);
        return tokens;
    }
    /**
     * Termine un parcours de pré-authentification : le compte est créé ou retrouvé, le
     * dossier rejoint, et la session adoptée dans le même mouvement.
     *
     * `skipRefresh` comme pour `login` : il n'y a rien à renouveler puisqu'aucune session
     * n'est encore ouverte, et tenter de le faire enverrait l'appelant sur une impasse.
     */
    async completerPreAuth(input) {
        const resultat = await this.request('/pre-auth/complete', {
            method: 'POST',
            body: input,
            skipRefresh: true,
        });
        this.adopt(resultat);
        return resultat;
    }
    async logout() {
        const refreshToken = this.refreshTokens.read();
        if (refreshToken || this.trustRefreshCookie) {
            // Sans jeton conservé mais avec cookie de confiance, le corps peut partir vide :
            // l'API lit le cookie, et le retire dans tous les cas.
            await this.request('/auth/logout', {
                method: 'POST',
                ...(refreshToken ? { body: { refreshToken } } : {}),
                skipRefresh: true,
            }).catch(() => undefined);
        }
        this.forget();
    }
    /** Restaure une session à partir du jeton conservé. Faux si aucune session valide. */
    async restore() {
        if (this.#accessToken)
            return true;
        if (!this.sessionPotentielle)
            return false;
        return this.refresh();
    }
    async request(path, options = {}) {
        const response = await this.send(path, options);
        if (response.status === 401 && !options.skipRefresh) {
            if (await this.refresh())
                return this.unwrap(await this.send(path, options));
            this.onSessionLost?.();
        }
        return this.unwrap(response);
    }
    /**
     * Ouvre le flux temps réel des messages d'un dossier, et le **tient ouvert**.
     *
     * Le jeton d'accès ne quitte jamais le client : c'est lui qui construit l'URL, parce
     * qu'un navigateur ne peut pas poser d'en-tête sur un WebSocket. Le faire remonter à
     * l'interface pour qu'elle assemble l'URL elle-même mettrait le jeton en circulation
     * dans du code qui n'a aucune raison de le manipuler.
     *
     * Le flux est un **raccourci** : il ne remplace pas la relève au chargement, et son
     * état ne dit rien de la présence de l'autre parent.
     *
     * **Il se rouvre seul, et c'est le point.** Un WebSocket ne survit pas à ce que la vie
     * d'une application mobile lui fait subir : Android coupe les connexions d'une
     * application passée en arrière-plan, un proxy ferme une connexion restée muette une
     * minute, un changement de Wi-Fi à 4G la casse net. Ouvert une fois pour toutes, le flux
     * mourait au premier de ces événements et ne revenait jamais — les pastilles de
     * nouveauté se figeaient alors pour le reste de la session, sans que rien ne le dise.
     *
     * `onReconnect` est appelé à chaque **ré**ouverture, jamais à la première : le temps
     * passé hors ligne est un trou dans ce qu'on a reçu, et il se comble en redemandant, pas
     * en attendant le message suivant. C'est ce qui distingue « le flux est revenu » de
     * « l'écran est à jour ».
     */
    openMessageStream(caseFileId, onMessage, options = {}) {
        let socket = null;
        let ferme = false;
        let ouvertUneFois = false;
        let echecs = 0;
        let minuterie = null;
        /**
         * Replanifie une ouverture, une seule fois par coupure.
         *
         * `close` et `error` arrivent souvent tous les deux pour la même coupure : sans ce
         * garde, chaque rupture doublerait le nombre de tentatives en vol, et une heure hors
         * réseau ouvrirait des centaines de connexions à la seconde où il revient.
         */
        const replanifier = () => {
            if (ferme || minuterie !== null)
                return;
            // 1s, 2s, 4s… plafonné à 30s. Le plafond compte plus que la progression : c'est lui
            // qui décide du temps qu'un parent passe avec un écran figé après une coupure longue.
            const attente = Math.min(1000 * 2 ** echecs, 30_000);
            echecs += 1;
            minuterie = setTimeout(() => {
                minuterie = null;
                void ouvrir();
            }, attente);
        };
        const ouvrir = async () => {
            // Un jeton expiré ferait échouer la négociation : on le renouvelle d'abord.
            if (!this.#accessToken)
                await this.restore();
            if (ferme)
                return;
            if (!this.#accessToken)
                return replanifier();
            // Le jeton d'accès ne descend **pas** dans l'URL : une URL se retrouve dans les
            // journaux du proxy, l'historique du navigateur et l'en-tête `Referer`. On échange
            // d'abord la session contre un billet de trente secondes, à usage unique, qui ne
            // vaut que pour ce dossier — périmé avant même d'être lu dans un journal.
            let ticket;
            try {
                ticket = (await this.request(`/case-files/${encodeURIComponent(caseFileId)}/messages/stream-ticket`, { method: 'POST' })).ticket;
            }
            catch {
                // Le flux est un raccourci : son échec ne casse rien, la relève au chargement et
                // le rafraîchissement manuel restent en place. On réessaie quand même — l'échec le
                // plus courant ici est l'absence de réseau, qui, elle, finit toujours par cesser.
                return replanifier();
            }
            if (ferme)
                return;
            // Base relative (`/api`, quand l'application et l'API partagent l'origine) : il en
            // reste la chaîne vide, et l'adresse part relative. C'est voulu — le constructeur
            // `WebSocket` la résout contre la page et bascule `https` en `wss` de lui-même.
            const base = this.baseUrl.replace(/^http/, 'ws').replace(/\/api$/, '');
            const url = `${base}/ws/messages?ticket=${encodeURIComponent(ticket)}`;
            const courant = this.webSocketFactory(url);
            socket = courant;
            courant.addEventListener('open', () => {
                echecs = 0;
                // La première ouverture ne rattrape rien : l'écran vient d'être monté, et ses
                // requêtes sont déjà parties. Les suivantes, elles, ouvrent sur un trou.
                if (ouvertUneFois)
                    options.onReconnect?.();
                ouvertUneFois = true;
            });
            courant.addEventListener('message', (evenement) => {
                try {
                    onMessage(JSON.parse(String(evenement.data)));
                }
                catch {
                    /* charge illisible : on l'ignore plutôt que de casser le fil */
                }
            });
            // Un socket périmé ne doit pas replanifier à la place de son remplaçant : après une
            // reprise, `socket` désigne le nouveau, et l'ancien se tait.
            const rompu = () => {
                if (socket === courant)
                    replanifier();
            };
            courant.addEventListener('close', rompu);
            courant.addEventListener('error', rompu);
        };
        void ouvrir();
        return {
            close: () => {
                ferme = true;
                if (minuterie !== null)
                    clearTimeout(minuterie);
                minuterie = null;
                socket?.close();
                socket = null;
            },
        };
    }
    // -------------------------------------------------------------------------
    /**
     * Adresse d'une route, à partir d'une base qui peut être **relative**.
     *
     * `baseUrl` vaut `/api` partout où l'application et l'API partagent une origine — ce
     * qui est le cas dès qu'un serveur mandataire est devant, donc en qualification et en
     * production ; seul le développement pointe une origine distincte. `new URL()` refuse
     * une adresse relative quand aucune base ne lui est donnée : la requête échouait alors
     * **avant de partir**, sur un `TypeError` synchrone que le `try` autour de `fetch` ne
     * voyait pas. Un appelant qui rattrape ses erreurs — l'écran de connexion, qui en
     * déduisait « aucun fournisseur disponible » — ne pouvait qu'attribuer la panne au
     * serveur.
     *
     * D'où l'assemblage en chaîne plutôt qu'un `new URL(cible, location.href)` : `fetch`
     * résout lui-même une adresse relative, et le client n'a pas à savoir s'il tourne dans
     * un navigateur ou dans React Native.
     */
    adresse(path, query) {
        const cible = `${this.baseUrl}${path}`;
        const parametres = new URLSearchParams();
        for (const [key, value] of Object.entries(query ?? {})) {
            if (value !== undefined)
                parametres.set(key, String(value));
        }
        const suffixe = parametres.toString();
        return suffixe === '' ? cible : `${cible}?${suffixe}`;
    }
    async send(path, options) {
        const url = this.adresse(path, options.query);
        const headers = { Accept: 'application/json' };
        // Rien pour `form` : c'est le navigateur qui pose le type et sa frontière.
        if (options.body !== undefined)
            headers['Content-Type'] = 'application/json';
        if (this.#accessToken)
            headers.Authorization = `Bearer ${this.#accessToken}`;
        const corps = options.form !== undefined
            ? { body: options.form }
            : options.body !== undefined
                ? { body: JSON.stringify(options.body) }
                : {};
        try {
            return await this.fetchImpl(url, {
                method: options.method ?? 'GET',
                headers,
                // Cookie de session : `include` dès qu'on s'y fie, y compris quand l'API est sur
                // une autre origine en développement — le CORS de l'API autorise explicitement
                // les requêtes crédentielées, origine par origine.
                ...(this.trustRefreshCookie ? { credentials: 'include' } : {}),
                ...corps,
            });
        }
        catch (cause) {
            throw new NetworkError(cause);
        }
    }
    /** Un seul renouvellement à la fois, partagé par toutes les requêtes en attente. */
    refresh() {
        this.refreshing ??= this.performRefresh().finally(() => {
            this.refreshing = null;
        });
        return this.refreshing;
    }
    async performRefresh() {
        const refreshToken = this.refreshTokens.read();
        if (!refreshToken && !this.trustRefreshCookie)
            return false;
        // Corps vide en mode cookie : l'API lit le jeton dans le cookie `httpOnly`, que ce
        // client ne voit pas et ne verra jamais — c'est précisément le but.
        const response = await this.send('/auth/refresh', {
            method: 'POST',
            ...(refreshToken ? { body: { refreshToken } } : {}),
            skipRefresh: true,
        });
        if (!response.ok) {
            this.forget();
            return false;
        }
        this.adopt((await response.json()));
        return true;
    }
    /**
     * Lit une réponse **binaire** — aujourd'hui la photo d'avatar d'un enfant.
     *
     * Une route authentifiée plutôt qu'une adresse signée posée dans un `src` : un portrait
     * d'enfant ne doit jamais être joignable par une URL qui circule, se copie et survit à
     * la fermeture de la session. Le prix est que l'interface doit fabriquer elle-même une
     * URL d'objet, et la libérer ; c'est un prix acceptable pour la donnée la plus sensible
     * du dossier.
     */
    async requestBinary(path, options = {}) {
        let response = await this.send(path, options);
        if (response.status === 401 && !options.skipRefresh) {
            if (await this.refresh()) {
                response = await this.send(path, options);
            }
            else {
                this.onSessionLost?.();
            }
        }
        if (!response.ok) {
            const body = (await response.json().catch(() => null));
            throw new ApiError(response.status, body);
        }
        return {
            bytes: await response.blob(),
            contentType: response.headers.get('content-type') ?? 'application/octet-stream',
        };
    }
    /**
     * Rapatrie un fichier depuis une **adresse signée absolue**, telle que l'API la rend
     * (`documents/download-link`, `attachments/…/download-link`, `exports/…/download-link`).
     *
     * Sans en-tête d'autorisation, et c'est voulu : le jeton porté par l'adresse **est**
     * l'autorisation, la route n'a pas de garde, et l'adresse ne vaut que pour son porteur
     * et pour quelques minutes.
     *
     * Passer par le client plutôt que par un `fetch` global garde une seule porte de sortie
     * réseau — celle qu'une doublure remplace en test. Et rapatrier plutôt qu'ouvrir dans un
     * onglet évite la fenêtre surgissante : `window.open` appelé après un `await` n'est plus
     * rattaché au clic, et les navigateurs le bloquent sans rien dire.
     */
    async telechargerLienSigne(url) {
        let response;
        try {
            response = await this.fetchImpl(url, { method: 'GET' });
        }
        catch (cause) {
            throw new NetworkError(cause);
        }
        if (!response.ok) {
            const body = (await response.json().catch(() => null));
            throw new ApiError(response.status, body);
        }
        return {
            bytes: await response.blob(),
            contentType: response.headers.get('content-type') ?? 'application/octet-stream',
        };
    }
    async unwrap(response) {
        if (response.status === 204)
            return undefined;
        if (!response.ok) {
            const body = (await response.json().catch(() => null));
            throw new ApiError(response.status, body);
        }
        return (await response.json());
    }
}
//# sourceMappingURL=client.js.map